// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  FormField,
  Input,
  KycStatusBadge,
  useToast,
} from "@chotca/ui";
import { KYC_STATUS_LABEL_VI } from "@chotca/config";
import { Eye, Mail, Plus, Power, PowerOff } from "lucide-react";
import {
  useActivateOrg,
  useAdminOrgs,
  useDeactivateOrg,
} from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { getSdk } from "@chotca/api-client";
import {
  DataTable,
  usePagination,
  type Column,
  type RowAction,
} from "@/components/data-table";
import { AddressPicker, type AddressValue } from "@/components/address-picker";

type OrgRow = {
  id: string;
  name: string;
  primaryAddress: {
    district: string;
    city: string;
  } | null;
  businessType: string;
  kycStatus: "NONE" | "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED";
  verifiedTier?: "NONE" | "TRUSTED" | "SUPER";
  isShadow?: boolean;
  ownerEmail: string | null;
  ownerPhone: string | null;
  postingCount: number;
  deactivatedAt: string | null;
};

export function OrgsClient() {
  const t = useTranslations("admin");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const filters = useTableFilters({
    search: { debounce: 300 },
    kycStatus: {},
    status: {},
  });

  const [createOpen, setCreateOpen] = React.useState(false);
  const [confirm, setConfirm] = React.useState<{
    org: OrgRow;
    mode: "deactivate" | "activate";
  } | null>(null);
  const deactivate = useDeactivateOrg();
  const activate = useActivateOrg();
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useAdminOrgs({
    search: filters.applied.search,
    kycStatus: filters.applied.kycStatus,
    status: filters.applied.status as "active" | "deactivated" | undefined,
    page,
    limit: pageSize,
  });

  const create = useMutation({
    mutationFn: async (form: {
      name: string;
      address: AddressValue;
      contactEmail?: string;
    }) => {
      const sdk = getSdk();
      return sdk.admin.createShadowOrg({
        name: form.name,
        contactEmail: form.contactEmail,
        address: form.address.address,
        district: form.address.district || undefined,
        city: form.address.city || undefined,
        provinceCode: form.address.provinceCode ?? undefined,
        lat: form.address.lat ?? undefined,
        lng: form.address.lng ?? undefined,
        mapUrl: form.address.mapUrl ?? undefined,
      } as Parameters<typeof sdk.admin.createShadowOrg>[0]);
    },
    onSuccess: (org) => {
      qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
      toast({ tone: "success", title: "Đã tạo doanh nghiệp tạm" });
      setCreateOpen(false);
      nav(`/orgs/${org.id}?tab=invitations`);
    },
    onError: () => toast({ tone: "error", title: "Tạo thất bại" }),
  });

  const list = (data?.items ?? []) as OrgRow[];

  const columns: Column<OrgRow>[] = [
    {
      key: "name",
      header: "Tên",
      cell: (o) => (
        <div className="flex flex-col">
          <span className="flex items-center gap-2 font-semibold text-ink">
            {o.name}
            {o.deactivatedAt ? <Badge tone="danger-soft">Đã tắt</Badge> : null}
          </span>
          <span className="text-[11px] text-ash">{o.businessType}</span>
        </div>
      ),
    },
    {
      key: "location",
      header: "Khu vực",
      cell: (o) => (
        <span className="text-xs">{o.primaryAddress?.district ?? "—"}</span>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      cell: (o) => (
        <span className="text-xs font-mono">
          {o.ownerEmail ?? o.ownerPhone ?? "—"}
        </span>
      ),
    },
    {
      key: "kyc",
      header: "KYC",
      cell: (o) => <KycStatusBadge status={o.kycStatus} />,
    },
    {
      key: "tier",
      header: "Hạng",
      cell: (o) =>
        o.verifiedTier && o.verifiedTier !== "NONE" ? (
          <Badge tone="ink">
            {o.verifiedTier === "SUPER" ? "SIÊU CẤP" : "UY TÍN"}
          </Badge>
        ) : (
          <span className="text-xs text-ash">—</span>
        ),
    },
    {
      key: "shadow",
      header: "Loại",
      cell: (o) =>
        o.isShadow ? (
          <Badge tone="sun">{t("shadowBadge")}</Badge>
        ) : (
          <Badge tone="lime">{t("claimedBadge")}</Badge>
        ),
    },
    {
      key: "postings",
      header: "# Tin",
      align: "right",
      cell: (o) => <span className="font-semibold">{o.postingCount}</span>,
    },
  ];

  const rowActions: RowAction<OrgRow>[] = [
    {
      key: "view",
      label: "Xem chi tiết",
      icon: Eye,
      onClick: (o) => nav(`/orgs/${o.id}`),
    },
    {
      key: "newPost",
      label: "Đăng tin cho doanh nghiệp này",
      icon: Plus,
      onClick: (o) => nav(`/postings/new?orgId=${o.id}`),
    },
    {
      key: "invite",
      label: "Gửi lời mời nhận bàn giao",
      icon: Mail,
      onClick: (o) => nav(`/orgs/${o.id}?tab=invitations`),
      show: (o) => o.isShadow === true,
    },
    {
      key: "deactivate",
      label: "Tắt doanh nghiệp",
      icon: PowerOff,
      destructive: true,
      onClick: (o) => setConfirm({ org: o, mode: "deactivate" }),
      show: (o) => !o.deactivatedAt,
    },
    {
      key: "activate",
      label: "Kích hoạt lại",
      icon: Power,
      onClick: (o) => setConfirm({ org: o, mode: "activate" }),
      show: (o) => !!o.deactivatedAt,
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("orgsTitle")}</h1>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            + {t("createShadowOrg")}
          </Button>
        }
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm theo tên doanh nghiệp..."
        />
        <FilterSelect
          value={filters.values.kycStatus ?? ""}
          onValueChange={(v) => filters.set("kycStatus", v)}
          placeholder="Mọi KYC"
          options={[
            { value: "NONE", label: KYC_STATUS_LABEL_VI.NONE },
            { value: "PENDING", label: KYC_STATUS_LABEL_VI.PENDING },
            { value: "APPROVED", label: KYC_STATUS_LABEL_VI.APPROVED },
            { value: "REJECTED", label: KYC_STATUS_LABEL_VI.REJECTED },
            { value: "EXPIRED", label: KYC_STATUS_LABEL_VI.EXPIRED },
          ]}
        />
        <FilterSelect
          value={filters.values.status ?? ""}
          onValueChange={(v) => filters.set("status", v)}
          placeholder="Đang hoạt động"
          options={[
            { value: "active", label: "Đang hoạt động" },
            { value: "deactivated", label: "Đã tắt" },
          ]}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(o) => o.id}
        rowActions={rowActions}
        onRowClick={(o) => nav(`/orgs/${o.id}`)}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <CreateShadowOrgDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(v) => create.mutate(v)}
        loading={create.isPending}
      />

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        destructive={confirm?.mode === "deactivate"}
        loading={deactivate.isPending || activate.isPending}
        title={
          confirm?.mode === "deactivate"
            ? "Tắt doanh nghiệp này?"
            : "Kích hoạt lại doanh nghiệp?"
        }
        description={
          confirm?.mode === "deactivate"
            ? "Doanh nghiệp sẽ bị ẩn khỏi danh sách và mọi tin đang mở sẽ bị đóng. Bạn có thể kích hoạt lại sau."
            : "Doanh nghiệp sẽ hiển thị trở lại. Các tin đã đóng sẽ không tự mở lại."
        }
        confirmLabel={confirm?.mode === "deactivate" ? "Tắt" : "Kích hoạt"}
        onConfirm={async () => {
          if (!confirm) return;
          try {
            if (confirm.mode === "deactivate") {
              await deactivate.mutateAsync(confirm.org.id);
              toast({ tone: "success", title: "Đã tắt doanh nghiệp" });
            } else {
              await activate.mutateAsync(confirm.org.id);
              toast({
                tone: "success",
                title: "Đã kích hoạt lại doanh nghiệp",
              });
            }
            setConfirm(null);
          } catch {
            toast({ tone: "error", title: "Thao tác thất bại" });
          }
        }}
      />
    </div>
  );
}

const EMPTY_ADDRESS: AddressValue = {
  address: "",
  wardCode: "",
  wardName: "",
  wardProvinceCode: "",
  district: "",
  city: "",
  provinceCode: null,
  provinceName: null,
  lat: null,
  lng: null,
  mapUrl: null,
};

function CreateShadowOrgDialog({
  open,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (v: {
    name: string;
    address: AddressValue;
    contactEmail?: string;
  }) => void;
  loading: boolean;
}) {
  const t = useTranslations("admin");
  const [name, setName] = React.useState("");
  const [address, setAddress] = React.useState<AddressValue>(EMPTY_ADDRESS);
  const [contactEmail, setContactEmail] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setName("");
      setAddress(EMPTY_ADDRESS);
      setContactEmail("");
    }
  }, [open]);

  const addressValid =
    !!address.address &&
    address.lat !== null &&
    address.lng !== null &&
    !!address.provinceCode;
  const emailValid =
    contactEmail.trim() === "" ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim());
  const canSubmit = !!name.trim() && addressValid && emailValid;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("createShadowOrg")}
      description={t("createShadowOrgDesc")}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Huỷ
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                name: name.trim(),
                address,
                contactEmail: contactEmail.trim() || undefined,
              })
            }
            loading={loading}
            disabled={!canSubmit}
          >
            Tạo
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <FormField label="Tên quán">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Quán cafe ABC"
          />
        </FormField>

        <FormField
          label="Email liên hệ"
          optional
          error={emailValid ? undefined : "Email không hợp lệ"}
          description="Nơi gửi email mời tiếp quản + báo cáo ứng viên định kỳ. Khác email chủ tài khoản. Có thể bổ sung sau."
        >
          <Input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="VD: quan@gmail.com"
          />
        </FormField>

        <AddressPicker
          label="Địa chỉ"
          required
          value={address}
          onChange={setAddress}
          error={
            !addressValid && address.address
              ? "Hãy dán link Google Maps để lấy toạ độ"
              : undefined
          }
        />

        <p className="rounded-md border border-info/30 bg-info/5 p-3 text-xs text-ink/70">
          Sau khi tạo xong, mở tab <strong>Lời mời nhận bàn giao</strong> để gửi
          email bàn giao cho chủ quán.
        </p>
      </div>
    </Dialog>
  );
}
